import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import Home from './pages/Home/Home';
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import VerifyEmail from './pages/VerifyEmail/VerifyEmail';
import CompleteProfile from './pages/CompleteProfile/CompleteProfile';
import Profile from './pages/Profile/Profile';
import EditProfile from './pages/EditProfile/EditProfile';
import AdminDashboard from './pages/Admin/AdminDashboard';
import Favorites from './pages/Favorites/Favorites';
import MyPosts from './pages/MyPosts/MyPosts';
import Help from './pages/Help/Help';
import AccountSettings from './pages/AccountSettings/AccountSettings';
import Terms from './pages/Terms/Terms';
import Privacy from './pages/Privacy/Privacy';
import Cookies from './pages/Cookies/Cookies';
import Guidelines from './pages/Guidelines/Guidelines';
import About from './pages/About/About';
import Careers from './pages/Careers/Careers';
import Press from './pages/Press/Press';
import ContactPage from './pages/Contact/Contact';
import NewsletterConfirm from './pages/Newsletter/NewsletterConfirm';
import Create from './pages/Create/Create';
import EditPost from './pages/EditPost/EditPost';
import ArticlePage from './pages/ArticlePage/ArticlePage';
import VideoPage from './pages/VideoPage/VideoPage';
import MemePage from './pages/MemePage/MemePage';
import Reels from './pages/Reels/Reels';
import Explore from './pages/Explore/Explore';
import Search from './pages/Search/Search';
import Library from './pages/Library/Library';
import Magazine from './pages/Magazine/Magazine';
import TV from './pages/TV/TV';
import TVShow from './pages/TV/TVShow';
import TVPlayer from './pages/TV/TVPlayer';
import Store from './pages/Store/Store';
import Games from './pages/Games/Games';
import Academy from './pages/Academy/Academy';
import CourseDetail from './pages/Academy/CourseDetail';
import LessonPlayer from './pages/Academy/LessonPlayer';
import ArticleModalRoute from './components/ModalRoutes/ArticleModalRoute';
import VideoModalRoute from './components/ModalRoutes/VideoModalRoute';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

function App() {
  return (
    <HelmetProvider>
      <Helmet>
        <title>Graphyra</title>
      </Helmet>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

function AppRoutes() {
  const location = useLocation();
  const state = location.state;
  const backgroundLocation = state && state.backgroundLocation;

  return (
    <>
      <Routes location={backgroundLocation || location}>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="verify-email/:token" element={<VerifyEmail />} />
          <Route 
            path="complete-profile" 
            element={
              <ProtectedRoute>
                <CompleteProfile />
              </ProtectedRoute>
            } 
          />
          <Route path="search" element={<Search />} />
          <Route path=":username" element={<Profile />} />
          <Route 
            path="admin" 
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="favorites" 
            element={
              <ProtectedRoute>
                <Favorites />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="my-posts" 
            element={
              <ProtectedRoute>
                <MyPosts />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="help" 
            element={
              <ProtectedRoute>
                <Help />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="settings/account" 
            element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="settings/profile" 
            element={
              <ProtectedRoute>
                <EditProfile />
              </ProtectedRoute>
            } 
          />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="cookies" element={<Cookies />} />
          <Route path="guidelines" element={<Guidelines />} />
          <Route path="about" element={<About />} />
          <Route path="team" element={<About />} />
          <Route path="careers" element={<Careers />} />
          <Route path="press" element={<Press />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="newsletter/confirm/:token" element={<NewsletterConfirm />} />
          
          <Route 
            path="create" 
            element={
              <ProtectedRoute>
                <Create />
              </ProtectedRoute>
            } 
          />

          <Route
            path="posts/edit/:id"
            element={
              <ProtectedRoute>
                <EditPost />
              </ProtectedRoute>
            }
          />
          
          {/* Content Pages */}
          <Route path="article/:slug" element={<ArticlePage />} />
          <Route path="video/:slug" element={<VideoPage />} />
          <Route path="meme/:slug" element={<MemePage />} />
          <Route path="reels" element={<Reels />} />
          <Route path="reels/:reelId" element={<Reels />} />
          
          {/* Explore - Estilo Encarta */}
          <Route path="explore" element={<Explore />} />
          <Route path="category/:categoryId" element={<Explore />} />
          <Route path="category/:categoryId/:subcategoryId" element={<Explore />} />
          
          {/* New Sections */}
          <Route path="library" element={<Library />} />
          <Route path="library/:collectionSlug" element={<Library />} />
          <Route path="magazine" element={<Magazine />} />
          <Route path="magazine/:editionId" element={<Magazine />} />
          <Route path="tv" element={<TV />} />
          <Route path="tv/show/:showSlug" element={<TVShow />} />
          <Route path="tv/watch/:showSlug/:episodeSlug" element={<TVPlayer />} />
          {/* Back-compat (old numeric route) */}
          <Route path="tv/watch/:showId/:seasonNumber/:episodeNumber" element={<TVPlayer />} />
          <Route path="store" element={<Store />} />
          <Route path="store/:productSlug" element={<Store />} />
          <Route path="games" element={<Games />} />
          <Route path="games/:gameId" element={<Games />} />
          
          {/* Academy - Learning Platform */}
          <Route path="academy" element={<Academy />} />
          <Route path="academy/course/:courseId" element={<CourseDetail />} />
          <Route path="academy/lesson/:courseId/:lessonSlug" element={<LessonPlayer />} />
          
          <Route path="*" element={<div className="container"><h1>404 - P├ígina no encontrada</h1></div>} />
        </Route>
      </Routes>

      {/* Modal overlays (only when we navigated from another page) */}
      {backgroundLocation && (
        <Routes>
          <Route path="article/:slug" element={<ArticleModalRoute />} />
          <Route path="video/:slug" element={<VideoModalRoute />} />
        </Routes>
      )}
    </>
  );
}

export default App;
